import { Flex, LayoutBox, Loader, BodyText, Link } from '@bamboohr/fabric';
import { RefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatLog } from 'src/pages/home/home.types';
import { useLlmStore } from 'src/store';
import BotResponse from '../BotResponse';
import SupplementCard from '../supplement/SupplementCard';
import CodeBlock from '../codeBlock/CodeBlock';
import FeedbackButtons from './FeedbackButtons';
import { styles } from './styles';

// A row with an id but no observation came back from an exhausted provider.
const PRIMARY_RESPONSE_UNAVAILABLE =
	'This answer could not be generated. Please try asking again.';

interface ChatLogRowProps {
	chat: ChatLog;
	chatLogRef: RefObject<HTMLDivElement>;
	hasPrinted: boolean;
	setHasPrinted: (hasPrinted: boolean) => void;
	isLast: boolean;
	isPrinting: boolean;
	setIsPrinting: (isPrinting: boolean) => void;
}

export const ChatLogRow = ({
	chat,
	chatLogRef,
	hasPrinted,
	setHasPrinted,
	isLast,
	isPrinting,
	setIsPrinting,
}: ChatLogRowProps) => {
	const llmLabel = useLlmStore((state) => state.llmLabel);
	const llmId = useLlmStore((state) => state.llmId);
	const supplementCards = chat.supplements?.map((supplement) => (
		<SupplementCard
			key={supplement.functionId}
			supplement={supplement}
			chatId={chat.id}
		/>
	));
	return (
		<div
			className="chatLog"
			ref={chatLogRef}
			id={`navPrompt-${chat.chatPrompt.replace(/[^a-zA-Z0-9]/g, '-')}`}
		>
			<Flex marginY="24px" justifyContent="center">
				<Flex flexDirection="column" alignItems="end" width={'100%'}>
					<div style={styles.chatPrompt}>
						<Flex padding="12px 16px">
							<ReactMarkdown
								className="react-markdown"
								remarkPlugins={[remarkGfm]}
								components={{
									code: CodeBlock,
									a: ({ node, children, ...props }) => (
										<a {...props} target="_blank" rel="noopener noreferrer">
											{children && children.length > 0 ? children : props.href}
										</a>
									),
								}}
							>
								{chat.chatPrompt}
							</ReactMarkdown>
						</Flex>
					</div>
				</Flex>
			</Flex>

			<div style={styles.botMessageMainContainer}>
				<Flex paddingY="24px" justifyContent="center" width="100%">
					<LayoutBox width={'100%'}>
						<Flex gap="12px" alignItems={'start'}>
							<img
								src="/images/bamboohrlogo.png"
								alt="BambooHR logo"
								style={styles.avatarImage}
							/>
							<div style={styles.nameContainer}>{llmLabel}</div>
							<FeedbackButtons chat={chat} />
						</Flex>
						{chat.botMessage != null ? (
							<div style={styles.botMessage}>
								<BotResponse
									response={chat.botMessage}
									chatLogRef={chatLogRef}
									hasPrinted={hasPrinted}
									setHasPrinted={setHasPrinted}
									sources={chat.sources}
									isLast={isLast}
									isPrinting={isPrinting}
									setIsPrinting={setIsPrinting}
								/>
								{llmId === 'answers-sales-questions' && (
									<Flex flexDirection="column" gap={2} marginTop={3} padding="12px 16px" backgroundColor="neutral-surface-weakest">
										<BodyText justify="start" size="extra-extra-small" color="neutral-weak">
											<span style={{ fontWeight: 'bold' }}>IMPORTANT DISCLAIMERS RELATED TO PROCESS AND POLICY:</span>
											<br />
											• Some processes and policies are actively under review or revision.<br />
											• The information provided by the AI for process/policy questions is not authoritative, and <span style={{ fontWeight: 'bold' }}>may not be used to resolve internal disputes</span>.<br />
											• Questions about lead distribution, opportunity disputes, routing or ownership rules should be submitted directly to{' '}
											<Link
												href="https://www.wrike.com/workspace.htm?acc=1622934#/forms?formid=158427"
												target="_blank"
												rel="noreferrer"
											>
												Sales Operations
											</Link>.<br />
											• <span style={{ fontWeight: 'bold' }}>Never</span> share internal policy or process information <span style={{ fontWeight: 'bold' }}>externally</span> with clients.
										</BodyText>
									</Flex>
								)}
								{/* Row-scoped: isPrinting is session-wide, so gating on it
								    alone unmounts every resolved card during a new answer. */}
								{(isLast ? !isPrinting : true) && supplementCards}
							</div>
						) : chat.id != null ? (
							<div style={styles.botMessage}>
								<BodyText size="small" color="error-strong">
									{PRIMARY_RESPONSE_UNAVAILABLE}
								</BodyText>
								{/* Supplements are independent of the primary, so any that
								    arrived still render here. */}
								{supplementCards}
							</div>
						) : (
							<LayoutBox marginTop="20px">
								<Loader />
							</LayoutBox>
						)}
					</LayoutBox>
				</Flex>
			</div>
		</div>
	);
};
